import { api } from '../api';
import { floorFlow, nextWork, SIDE_FLOW, type FlowStep } from '../flow';
import { fmt } from '../format';
import { useSession } from '../session';
import { stockWaitingBanner } from '../stockWaiting';
import type { FloorSummary } from '../types';
import { ErrorBanner, Spinner, useLoad } from '../ui';
import { Icon, type IconName } from '../ui/icons';
import { NextStep } from '../ui/NextStep';
import { useUrgent } from './Urgent';

interface Tile {
  readonly label: string;
  readonly value: number;
  readonly hint: string;
  readonly href: string;
  readonly alert?: boolean;
}

export function Dashboard() {
  const { warehouseId, me, isSupervisor } = useSession();
  const summary = useLoad(() => api<FloorSummary>('GET', `/api/wms/summary${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], 10_000);
  // the red bar's list: what must be done now about an order (a carrier changed, a cancel)
  const urgent = useUrgent()?.actions?.length ?? 0;
  if (summary.error) return <ErrorBanner message={summary.error} onRetry={() => void summary.reload()} />;
  if (!summary.data) return <Spinner />;
  const s = summary.data;
  const o = (status: keyof FloorSummary['orders']) => s.orders[status] ?? 0;

  const flow: readonly { title: string; icon: IconName; tiles: readonly Tile[] }[] = [
    {
      title: 'Giriş',
      icon: 'inbox',
      tiles: [
        { label: 'Açık mal kabul', value: s.openInbound, hint: 'Beklenen veya kabulü süren ASN', href: '#/kabul' },
        { label: 'Yerleştirme', value: s.putawayTasks, hint: 'Kabul alanından göze', href: '#/yerlestirme' },
        { label: 'Rafa iade', value: s.returnTasks, hint: 'İptal ve müşteri iadesinden dönen sağlam ürünler', href: '#/yerlestirme', alert: s.returnTasks > 0 },
        { label: 'Beklenen iade', value: s.expectedReturns, hint: 'Satıcının bildirdiği, yoldaki müşteri iadeleri', href: '#/iade' },
      ],
    },
    {
      title: 'Çıkış',
      icon: 'truck',
      tiles: [
        { label: 'Toplamaya hazır', value: o('allocated'), hint: 'Stoğu rezerve edilmiş sipariş', href: '#/toplama' },
        { label: 'Açık rota', value: s.openRoutes, hint: 'Yürünmekte olan toplama rotası', href: '#/toplama' },
        { label: 'Paketlemeye hazır', value: o('picked') + o('packing'), hint: 'Toplanmış, masada bekliyor', href: '#/paketleme' },
        { label: 'Kargoya hazır', value: o('packed'), hint: 'Paketlendi, kargo teslimi bekliyor', href: '#/sevkiyat' },
        { label: 'Bugün kargolanan', value: s.shippedToday, hint: 'Kargo firmasına teslim edilen', href: '#/siparisler' },
      ],
    },
    {
      title: 'Dikkat',
      icon: 'alert',
      tiles: [
        { label: 'Acil iş', value: urgent, hint: 'Kargo firması değişti ya da sipariş iptal edildi: hemen yapılacak', href: '#/acil', alert: urgent > 0 },
        { label: 'Sorunlu sipariş', value: o('on_hold'), hint: 'Eksik toplama, eşleşmeyen ürün', href: '#/sorunlu', alert: o('on_hold') > 0 },
        { label: 'Sayılacak göz', value: s.counts.open, hint: 'Eksik toplanan veya sayım emri verilen, kilitli', href: '#/sayim', alert: s.counts.open > 0 },
        { label: 'Sayım farkı onayı', value: s.counts.review, hint: 'Sistemle tutmayan sayımlar, sorumlu onayı bekliyor', href: '#/sayim', alert: s.counts.review > 0 },
        { label: "SKT'si yaklaşan ürün", value: s.expiringProducts, hint: '30 gün içinde dolan, raf ömrü yetmeyen veya geçmiş lotlar', href: '#/stok?skt=yakin', alert: s.expiringProducts > 0 },
        { label: 'Stok bekleyen', value: o('backordered'), hint: 'Ürün depodaysa stoğa girin', href: '#/stok-bekleyen', alert: o('backordered') > 0 },
        { label: 'Rezerve bekleyen', value: o('pending'), hint: 'Arka planda rezerve ediliyor', href: '#/siparisler' },
      ],
    },
  ];

  const ops = s.ops?.critical ?? 0;
  // orders waiting for stock, for the staff as for the supervisors: the goods may be here unentered
  const waiting = stockWaitingBanner(s.stockWaiting, new Date());
  return (
    <>
      {ops > 0 && (
        <a className="banner bad" href="#/yonetim/sistem" style={{ textDecoration: 'none' }}>
          <Icon name="pulse" />
          <div style={{ flex: 1 }}>
            {ops === 1 ? 'Arka planda bir sorun var' : `Arka planda ${ops} sorun var`}: pazaryeri senkronu, etiket veya kuyruk.
            <div style={{ fontWeight: 500 }}>Ayrıntı ve yeniden deneme için Sistem durumu ekranına dokunun.</div>
          </div>
        </a>
      )}
      {waiting && (
        <a className="banner warn" href="#/stok-bekleyen" style={{ textDecoration: 'none' }}>
          <Icon name="clock" />
          <div style={{ flex: 1 }}>
            {waiting}
            <div style={{ fontWeight: 500 }}>Ürün depodaysa mal kabulden stoğa girin; değilse satıcının gönderisi bekleniyor. Liste için dokunun.</div>
          </div>
        </a>
      )}
      <div className="greeting">
        <h2>Merhaba {me.fullName.split(' ')[0]}</h2>
        <p className="muted">{isSupervisor ? 'Depodaki işler aşağıda; bir kutuya dokunarak ilgili ekrana geçin.' : 'Sıradaki iş aşağıda; bir adıma dokunarak ekranına geçin.'}</p>
      </div>
      <FlowGuide summary={s} />
      {/* the staff see Giriş and Çıkış in the flow above; supervisors keep every tile */}
      {flow.filter((group) => isSupervisor || group.title === 'Dikkat').map((group) => (
        <section key={group.title} className="stack">
          <h2 className="section-h">
            <span className="section-icon">
              <Icon name={group.icon} size={16} />
            </span>
            {group.title}
          </h2>
          <div className="grid tiles">
            {group.tiles.map((t) => (
              <a key={t.label} href={t.href} className={`card kpi${t.alert ? ' alert' : ''}${t.value === 0 ? ' zero' : ''}`}>
                <span className="n">{fmt.n(t.value)}</span>
                <span className="l">{t.label}</span>
                <span className="h">{t.hint}</span>
              </a>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/**
 * "Sıradaki iş": the one work to do next by the rule in flow.ts, then the floor's five steps with what
 * waits at each (one row per step on a phone) and the returns and counts beside them.
 */
function FlowGuide({ summary }: { summary: FloorSummary }) {
  const next = nextWork(summary);
  const row = (step: FlowStep, n?: number) => {
    const now = next !== null && step.key === next.key;
    return (
      <li key={step.key}>
        <a href={`#${step.path}`} className={`flow-step${now ? ' now' : ''}${step.waiting === 0 ? ' zero' : ''}`} aria-current={now ? 'step' : undefined}>
          {n !== undefined && (
            <span className="flow-n" aria-hidden>
              {n}
            </span>
          )}
          <span className="flow-icon" aria-hidden>
            <Icon name={step.icon} size={20} />
          </span>
          <span className="flow-what">
            <b>{step.label}</b>
            <span>{step.text}</span>
          </span>
          {step.waiting > 0 && (
            <span className="flow-count" aria-hidden>
              {fmt.n(step.waiting)}
            </span>
          )}
        </a>
      </li>
    );
  };
  return (
    <div className="flow-guide">
      {next ? (
        <NextStep kicker="Sıradaki iş" title={`Sırada: ${next.what}`} steps={[next.how]} go={{ label: next.action, to: next.path, icon: next.icon }} />
      ) : (
        <NextStep kicker="Sıradaki iş" tone="ok" title="Bekleyen iş yok" steps={['Yeni sipariş ya da gönderi gelince burada görünür.']} />
      )}
      <section className="card flow-card" aria-label="İşlerin sırası">
        <div className="card-h">
          <h2>İşlerin sırası</h2>
          <span className="sub">Ürün depoda bu sırayla ilerler</span>
        </div>
        <ol className="flow-steps">{floorFlow(summary).map((step, i) => row(step, i + 1))}</ol>
        <h3 className="flow-side-h">Yanında</h3>
        <ul className="flow-steps flow-extra">{floorFlow(summary, SIDE_FLOW).map((step) => row(step))}</ul>
      </section>
    </div>
  );
}
